// Repozitář PEF ČZU — vlastní podoba sekce DOI v depozitním formuláři.
//
// PROČ TO TU JE (6.8.2026)
// -----------------------
// V našem dvoustupňovém modelu DOI nepřiděluje vkladatel, ale vzniká
// automaticky ve chvíli, kdy práci schválí data steward. Sekce DOI ale musí
// ve formuláři zůstat — někdo přichází s prací, která DOI už má (typicky
// vydaný článek), a musí ho mít kam zadat.
//
// Potřebujeme tedy: přepínač „Ano, už mám / Ne, potřebuji" zachovat,
// u „Ano" nechat políčko na vlastní DOI, a u „Ne" nezobrazit nic než
// vysvětlení. Konfigurací to nejde: v upstreamu je
//     canBeManagedAndUnmanaged = canBeManaged && canBeUnmanaged
// takže vypnutí `can_be_managed` shodí i samotný přepínač a formulář pak po
// každém vyžaduje vlastní DOI. (Navíc `get_form_pids_config()` má
// `can_be_managed = True` natvrdo, s upstream poznámkou „FIXME".)
//
// Proto je tu kopie `RequiredPIDField` z invenio-rdm-records, ve které je
// větev „Ne, potřebuji" nahrazena hláškou. Podkomponenty se importují
// z upstreamu, aby se kopírovala jen tahle jedna třída.
//
// ⚠️ ÚDRŽBA PŘI UPGRADU: tohle je kopie cizího kódu. Po upgradu InvenioRDM
// porovnat s originálem:
//   invenio_rdm_records/assets/semantic-ui/js/invenio_rdm_records/src/
//     deposit/fields/Identifiers/PIDField/RequiredPIDField.js
// Kdyby upstream opravil ten FIXME a šlo to nastavit konfigurací, tenhle
// soubor smazat a v mapping.js zrušit přepis.

import _debounce from "lodash/debounce";
import PropTypes from "prop-types";
import React, { Component, Fragment } from "react";
import { FastField } from "formik";
import { FieldLabel } from "react-invenio-forms";
import { Form, Message } from "semantic-ui-react";
import { PIDField } from "@js/invenio_rdm_records";
import {
  ManagedUnmanagedSwitch,
  UnmanagedIdentifierCmp,
} from "@js/invenio_rdm_records/src/deposit/fields/Identifiers/PIDField/components";
import { getFieldErrors } from "@js/invenio_rdm_records/src/deposit/fields/Identifiers/PIDField/components/helpers";

const PROVIDER_EXTERNAL = "external";
const UPDATE_PID_DEBOUNCE_MS = 200;

/** Kopie RequiredPIDField bez možnosti rezervovat DOI. */
export class CzuRequiredPIDField extends Component {
  constructor(props) {
    super(props);

    const { canBeManaged, canBeUnmanaged, record, field } = this.props;
    this.canBeManagedAndUnmanaged = canBeManaged && canBeUnmanaged;
    const value = field?.value;
    const isDraft = record?.is_draft === true;

    // Volba „mám / nemám DOI" se do konceptu UKLÁDÁ — jako `pids.doi.provider`.
    // `PIDsComponent.update_draft` zapisuje `record.pids = pids_data` doslova,
    // takže i varianta „Ano, již mám" s prázdným polem zůstane uložená jako
    // `{ identifier: "", provider: "external" }`.
    //
    // Původní kód se ale ptal na `hasIdentifier`, takže bez vyplněné hodnoty
    // volbu neviděl a spadl na výchozí nastavení — a uživatel po znovuotevření
    // konceptu našel vybranou opačnou možnost, než jakou zvolil.
    //
    // Upstreamu to nevadí, protože při volbě „Ne, potřebuji" DOI rovnou
    // rezervuje a identifikátor tedy existuje. My rezervaci nechceme (to je
    // smysl celé téhle komponenty), takže se řídíme uloženým `provider`.
    //
    // Pozn.: volba „Ne, potřebuji" se ukládá jako PRÁZDNÉ `pids` — uložit ji
    // výslovně nejde, protože `pids/manager.py:create()` by při publikaci na
    // takovém záznamu selhal. Nese ji proto výchozí hodnota `default_selected`
    // = "no" (viz invenio.cfg), na kterou se níž spadne.
    const storedProvider = value?.provider;
    const isManagedSelected =
      isDraft && storedProvider ? storedProvider !== PROVIDER_EXTERNAL : undefined;

    this.state = {
      isManagedSelected: isManagedSelected,
    };
  }

  onExternalIdentifierChanged = (identifier) => {
    const { form, fieldPath } = this.props;

    const pid = {
      identifier: identifier,
      provider: PROVIDER_EXTERNAL,
    };

    this.debounced && this.debounced.cancel();
    this.debounced = _debounce(() => {
      form.setFieldValue(fieldPath, pid);
    }, UPDATE_PID_DEBOUNCE_MS);
    this.debounced();
  };

  render() {
    const { isManagedSelected } = this.state;
    const {
      canBeManaged,
      canBeUnmanaged,
      form,
      fieldPath,
      fieldLabel,
      isEditingPublishedRecord,
      pidLabel,
      pidIcon,
      pidPlaceholder,
      required,
      unmanagedHelpText,
      field,
      record,
    } = this.props;

    const { doiDefaultSelection } = this.props;

    const value = field.value || {};
    const currentIdentifier = value.identifier || "";
    const currentProvider = value.provider || "";

    let managedIdentifier = "",
      unmanagedIdentifier = "";
    if (currentIdentifier !== "") {
      const isProviderExternal = currentProvider === PROVIDER_EXTERNAL;
      managedIdentifier = !isProviderExternal ? currentIdentifier : "";
      unmanagedIdentifier = isProviderExternal ? currentIdentifier : "";
    }

    const hasManagedIdentifier = managedIdentifier !== "";
    const doi = record?.pids?.doi?.identifier || "";
    const parentDoi = record.parent?.pids?.doi?.identifier || "";

    const hasDoi = doi !== "";
    const isDoiCreated = currentIdentifier !== "";

    const _isManagedSelected =
      isManagedSelected === undefined
        ? hasManagedIdentifier ||
          (currentIdentifier === "" && doiDefaultSelection === "no")
        : isManagedSelected;

    const fieldError = getFieldErrors(form, fieldPath);

    return (
      <>
        <Form.Field
          required={required || parentDoi !== ""}
          error={fieldError ? true : false}
        >
          <FieldLabel htmlFor={fieldPath} icon={pidIcon} label={fieldLabel} />
        </Form.Field>

        {this.canBeManagedAndUnmanaged && (
          <ManagedUnmanagedSwitch
            disabled={
              (isEditingPublishedRecord || hasManagedIdentifier) &&
              (hasDoi || isDoiCreated)
            }
            isManagedSelected={_isManagedSelected}
            onManagedUnmanagedChange={(userSelectedManaged) => {
              if (userSelectedManaged) {
                form.setFieldValue("pids", {});
              } else {
                this.onExternalIdentifierChanged("");
              }
              form.setFieldError(fieldPath, false);
              this.setState({
                isManagedSelected: userSelectedManaged,
              });
            }}
            pidLabel={pidLabel}
          />
        )}

        {/* ZMĚNA OPROTI UPSTREAMU: místo políčka a tlačítka „Získejte DOI nyní!"
            jen vysvětlení. Rezervovat DOI smí podle našich oprávnění pouze
            kurátor komunity nebo data steward — vkladateli by tlačítko stejně
            skončilo chybou. */}
        {canBeManaged && _isManagedSelected && (
          <Message info size="small">
            <Message.Content>
              DOI přidělí repozitář automaticky ve chvíli, kdy vaši práci schválí
              data steward. Nemusíte nic vyplňovat.
            </Message.Content>
          </Message>
        )}

        {canBeUnmanaged && !_isManagedSelected && (
          <UnmanagedIdentifierCmp
            identifier={unmanagedIdentifier}
            onIdentifierChanged={(identifier) => {
              this.onExternalIdentifierChanged(identifier);
            }}
            form={form}
            fieldPath={fieldPath}
            pidPlaceholder={pidPlaceholder}
            helpText={unmanagedHelpText}
          />
        )}
      </>
    );
  }
}

CzuRequiredPIDField.propTypes = {
  field: PropTypes.object,
  form: PropTypes.object.isRequired,
  canBeManaged: PropTypes.bool.isRequired,
  canBeUnmanaged: PropTypes.bool.isRequired,
  fieldPath: PropTypes.string.isRequired,
  fieldLabel: PropTypes.string.isRequired,
  isEditingPublishedRecord: PropTypes.bool.isRequired,
  pidIcon: PropTypes.string,
  pidLabel: PropTypes.string.isRequired,
  pidPlaceholder: PropTypes.string,
  pidType: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired,
  unmanagedHelpText: PropTypes.string,
  record: PropTypes.object.isRequired,
  doiDefaultSelection: PropTypes.string,
};

CzuRequiredPIDField.defaultProps = {
  field: undefined,
  pidIcon: "barcode",
  pidPlaceholder: "",
  unmanagedHelpText: null,
  doiDefaultSelection: undefined,
};

/**
 * Náhrada za `InvenioAppRdm.Deposit.PIDField.container`.
 *
 * Vykresluje totéž co upstream (viz RDMDepositForm.js, sekce
 * „basic-information-section"), jen pro povinné DOI použije naši variantu.
 * Když by DOI povinné nebylo, necháme práci původní komponentě — tu větev
 * neupravujeme a nemá smysl ji duplikovat.
 */
export const CzuPIDFieldContainer = ({ config, record }) => (
  <Fragment>
    {config.pids.map((pid) => {
      const props = {
        btnLabelDiscardPID: pid.btn_label_discard_pid,
        btnLabelGetPID: pid.btn_label_get_pid,
        canBeManaged: pid.can_be_managed,
        canBeUnmanaged: pid.can_be_unmanaged,
        optionalDOItransitions: pid.optional_doi_transitions,
        fieldPath: `pids.${pid.scheme}`,
        fieldLabel: pid.field_label,
        isEditingPublishedRecord: record.is_published === true,
        managedHelpText: pid.managed_help_text,
        pidLabel: pid.pid_label,
        pidPlaceholder: pid.pid_placeholder,
        pidType: pid.scheme,
        unmanagedHelpText: pid.unmanaged_help_text,
        doiDefaultSelection: pid.default_selected,
        required: config.is_doi_required,
        record: record,
      };

      return (
        <Fragment key={pid.scheme}>
          {config.is_doi_required ? (
            <FastField
              name={`pids.${pid.scheme}`}
              component={CzuRequiredPIDField}
              {...props}
            />
          ) : (
            <PIDField {...props} />
          )}
        </Fragment>
      );
    })}
  </Fragment>
);

CzuPIDFieldContainer.propTypes = {
  config: PropTypes.object.isRequired,
  record: PropTypes.object.isRequired,
};
